<template>
  <div>
    <Head title="Avisos de Cobro" />

    <!-- Header -->
    <div class="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
      <div>
        <div class="flex items-center gap-2 text-sm text-gray-400 mb-1">
          <Link href="/cobranzas" class="hover:text-orange-500 transition-colors">Cobranzas</Link>
          <span>/</span>
          <span class="text-gray-600 font-medium">Avisos de Cobro</span>
        </div>
        <h1 class="text-3xl font-bold text-gray-800">Avisos de Cobro</h1>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <!-- Filtro estado -->
        <select
          v-model="form.state"
          class="form-select rounded-lg border-gray-200 text-sm focus:ring-orange-500 focus:border-orange-500 bg-white shadow-sm"
        >
          <option value="">Todos los estados</option>
          <option value="estimate">Pendiente de Pago</option>
          <option value="request">Confirmado</option>
          <option value="finish">Pagado</option>
          <option value="cancel">Anulado</option>
        </select>

        <!-- Buscador -->
        <div class="relative">
          <input
            v-model="form.search"
            type="text"
            placeholder="Buscar cliente o N°..."
            class="block pl-8 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-orange-500 focus:border-orange-500 shadow-sm w-52"
          />
          <i class="fa fa-search absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
        </div>

        <!-- Nuevo aviso -->
        <Link
          href="/cobranzas/aviso/create"
          class="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg flex items-center gap-2 transition-colors font-medium shadow-sm text-sm"
        >
          <i class="fa fa-plus" />
          Nuevo Aviso
        </Link>
      </div>
    </div>

    <!-- Stats rápidos -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
      <div class="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
        <p class="text-xs text-gray-400 font-medium uppercase tracking-wider mb-1">Total emitidos</p>
        <h3 class="text-2xl font-bold text-gray-800">{{ stats.total_count }}</h3>
      </div>
      <div class="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
        <p class="text-xs text-yellow-500 font-medium uppercase tracking-wider mb-1">Pendientes</p>
        <h3 class="text-2xl font-bold text-yellow-600">{{ stats.pending_count }}</h3>
        <p class="text-xs text-gray-400 mt-0.5">{{ formatCurrency(stats.pending_amount) }}</p>
      </div>
      <div class="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
        <p class="text-xs text-green-500 font-medium uppercase tracking-wider mb-1">Pagados</p>
        <h3 class="text-2xl font-bold text-green-600">{{ stats.paid_count }}</h3>
        <p class="text-xs text-gray-400 mt-0.5">{{ formatCurrency(stats.paid_amount) }}</p>
      </div>
      <div class="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
        <p class="text-xs text-red-400 font-medium uppercase tracking-wider mb-1">Anulados</p>
        <h3 class="text-2xl font-bold text-red-500">{{ stats.cancelled_count }}</h3>
      </div>
    </div>

    <!-- Tabla desktop -->
    <div class="hidden md:block bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <table class="w-full text-left text-sm">
        <thead>
          <tr class="bg-gray-50/50 border-b border-gray-100">
            <th class="py-4 px-5 text-xs font-bold text-gray-400 uppercase tracking-wider">N° Aviso</th>
            <th class="py-4 px-5 text-xs font-bold text-gray-400 uppercase tracking-wider">Cliente</th>
            <th class="py-4 px-5 text-xs font-bold text-gray-400 uppercase tracking-wider">Fecha</th>
            <th class="py-4 px-5 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Total</th>
            <th class="py-4 px-5 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Pagado</th>
            <th class="py-4 px-5 text-xs font-bold text-gray-400 uppercase tracking-wider text-right">Saldo</th>
            <th class="py-4 px-5 text-xs font-bold text-gray-400 uppercase tracking-wider text-center">Estado</th>
            <th class="py-4 px-5 text-xs font-bold text-gray-400 uppercase tracking-wider text-center">Acciones</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-50">
          <tr
            v-for="aviso in avisos.data"
            :key="aviso.id"
            class="hover:bg-orange-50/30 transition-colors group"
          >
            <td class="py-4 px-5">
              <span class="font-mono font-semibold text-gray-700">{{ aviso.number_order }}</span>
            </td>
            <td class="py-4 px-5">
              <div class="font-semibold text-gray-800 group-hover:text-orange-600 transition-colors">
                {{ aviso.client?.name }}
              </div>
              <div class="text-xs text-gray-400">{{ aviso.client?.identification }}</div>
            </td>
            <td class="py-4 px-5 text-gray-500 text-sm">
              {{ formatDate(aviso.created_at) }}
            </td>
            <td class="py-4 px-5 text-right font-semibold text-gray-800">
              {{ formatCurrency(aviso.total) }}
            </td>
            <td class="py-4 px-5 text-right text-green-600 font-medium">
              {{ formatCurrency(aviso.balance) }}
            </td>
            <td class="py-4 px-5 text-right font-bold" :class="aviso.total - aviso.balance > 0 ? 'text-red-600' : 'text-green-600'">
              {{ formatCurrency(aviso.total - aviso.balance) }}
            </td>
            <td class="py-4 px-5 text-center">
              <span
                :class="getStateClass(aviso.order_state)"
                class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold"
              >
                <i class="fa fa-circle text-[6px]" />
                {{ getStateLabel(aviso.order_state) }}
              </span>
            </td>
            <td class="py-4 px-5 text-center">
              <div class="flex items-center justify-center gap-1">
                <!-- Ver orden completa -->
                <Link
                  :href="`/orders/${aviso.id}`"
                  class="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                  title="Ver orden completa"
                >
                  <i class="fa fa-eye" />
                </Link>
                <!-- PDF aviso -->
                <a
                  :href="`/cobranzas/aviso/${aviso.id}/pdf`"
                  target="_blank"
                  class="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                  title="Descargar PDF del aviso"
                >
                  <i class="fa fa-file-pdf" />
                </a>
                <!-- WhatsApp si tiene teléfono -->
                <a
                  v-if="aviso.client?.phone"
                  :href="getWhatsAppLink(aviso)"
                  target="_blank"
                  class="inline-flex items-center justify-center w-8 h-8 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all"
                  title="Enviar por WhatsApp"
                >
                  <i class="fab fa-whatsapp" />
                </a>
              </div>
            </td>
          </tr>

          <tr v-if="!avisos.data?.length">
            <td colspan="8" class="py-20 text-center">
              <div class="flex flex-col items-center gap-2 text-gray-400">
                <i class="fa fa-file-invoice text-4xl opacity-30" />
                <p class="text-base font-medium text-gray-500">No hay avisos de cobro</p>
                <p class="text-sm">Crea el primero desde el botón "Nuevo Aviso"</p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Cards mobile -->
    <div class="grid grid-cols-1 gap-4 md:hidden">
      <div
        v-for="aviso in avisos.data"
        :key="aviso.id"
        class="bg-white p-5 rounded-2xl shadow-sm border border-gray-100"
      >
        <div class="flex justify-between items-start mb-3">
          <div>
            <div class="font-mono font-semibold text-gray-700 text-sm">{{ aviso.number_order }}</div>
            <div class="font-semibold text-gray-900">{{ aviso.client?.name }}</div>
            <div class="text-xs text-gray-400">{{ formatDate(aviso.created_at) }}</div>
          </div>
          <span :class="getStateClass(aviso.order_state)" class="px-2 py-1 rounded-full text-xs font-bold">
            {{ getStateLabel(aviso.order_state) }}
          </span>
        </div>

        <div class="grid grid-cols-3 gap-2 text-center text-sm mb-4 bg-gray-50 rounded-xl p-3">
          <div>
            <div class="text-xs text-gray-400">Total</div>
            <div class="font-bold text-gray-800">{{ formatCurrency(aviso.total) }}</div>
          </div>
          <div>
            <div class="text-xs text-gray-400">Pagado</div>
            <div class="font-bold text-green-600">{{ formatCurrency(aviso.balance) }}</div>
          </div>
          <div>
            <div class="text-xs text-gray-400">Saldo</div>
            <div class="font-bold" :class="aviso.total - aviso.balance > 0 ? 'text-red-600' : 'text-green-600'">
              {{ formatCurrency(aviso.total - aviso.balance) }}
            </div>
          </div>
        </div>

        <div class="flex gap-2">
          <Link :href="`/orders/${aviso.id}`" class="flex-1 text-center py-2 bg-blue-50 text-blue-600 rounded-lg text-xs font-semibold hover:bg-blue-100 transition">
            <i class="fa fa-eye mr-1" /> Ver Orden
          </Link>
          <a :href="`/cobranzas/aviso/${aviso.id}/pdf`" target="_blank" class="flex-1 text-center py-2 bg-red-50 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-100 transition">
            <i class="fa fa-file-pdf mr-1" /> PDF
          </a>
          <a v-if="aviso.client?.phone" :href="getWhatsAppLink(aviso)" target="_blank" class="flex-1 text-center py-2 bg-green-50 text-green-600 rounded-lg text-xs font-semibold hover:bg-green-100 transition">
            <i class="fab fa-whatsapp mr-1" /> WA
          </a>
        </div>
      </div>

      <div v-if="!avisos.data?.length" class="py-12 text-center text-gray-400 italic bg-white rounded-2xl border border-gray-100">
        <i class="fa fa-file-invoice text-3xl opacity-30 block mb-2" />
        No hay avisos de cobro emitidos
      </div>
    </div>

    <!-- Paginación -->
    <pagination v-if="avisos.meta?.links" :links="avisos.meta.links" class="mt-6" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'

export default {
  name: 'AvisosCobroIndex',
  components: { Head, Link, Pagination },
  layout: Layout,

  props: {
    avisos: Object,
    stats: Object,
    filters: Object,
  },

  data() {
    return {
      form: {
        search: this.filters?.search || '',
        state: this.filters?.state || '',
      },
    }
  },

  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/cobranzas/avisos', pickBy(this.form), { preserveState: true })
      }, 300),
    },
  },

  methods: {
    formatCurrency(value) {
      return new Intl.NumberFormat('es-VE', {
        style: 'currency', currency: 'USD', minimumFractionDigits: 2,
      }).format(value || 0)
    },

    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('es-VE', {
        day: '2-digit', month: 'short', year: 'numeric',
      })
    },

    getStateLabel(state) {
      const map = {
        estimate: 'Pendiente',
        request:  'Confirmado',
        finish:   'Pagado',
        cancel:   'Anulado',
        pay:      'En pago',
        delivery: 'Entrega',
      }
      return map[state] || state
    },

    getStateClass(state) {
      const map = {
        estimate: 'bg-yellow-100 text-yellow-700',
        request:  'bg-blue-100 text-blue-700',
        finish:   'bg-green-100 text-green-700',
        cancel:   'bg-red-100 text-red-700',
        pay:      'bg-teal-100 text-teal-700',
        delivery: 'bg-purple-100 text-purple-700',
      }
      return map[state] || 'bg-gray-100 text-gray-700'
    },

    getWhatsAppLink(aviso) {
      const phone = (aviso.client?.phone || '').replace(/\D/g, '')
      const text = encodeURIComponent(
        `Hola ${aviso.client?.name}, te hacemos llegar el aviso de cobro N° ${aviso.number_order} por un monto de ${this.formatCurrency(aviso.total)}. Por favor regulariza tu pago.`,
      )
      return `https://wa.me/${phone}?text=${text}`
    },
  },
}
</script>
